export const BROWSE_PAGE_SIZE = 20;

export const CARD_CONDITION_OPTIONS = [
  "Near Mint",
  "Lightly Played",
  "Moderately Played",
  "Heavily Played",
  "Damaged",
] as const;

export const GRADING_COMPANY_OPTIONS = [
  { value: "raw", label: "Raw (Ungraded)" },
  { value: "psa", label: "PSA" },
  { value: "bgs", label: "BGS" },
  { value: "cgc", label: "CGC" },
  { value: "tag", label: "TAG" },
  { value: "sgc", label: "SGC" },
  { value: "other", label: "Other" },
] as const;

export const GRADE_NUMBER_OPTIONS = [
  "10",
  "9.5",
  "9",
  "8.5",
  "8",
  "7.5",
  "7",
  "6",
  "5",
  "4",
  "3",
  "2",
  "1",
] as const;

export type SharedBrowseFilters = {
  q: string;
  zipCode: string;
  game: string;
  verifiedOnly: boolean;
  gradedOnly: boolean;
};

export type TradeBrowseFilters = SharedBrowseFilters;

export type BountyBrowseFilters = SharedBrowseFilters;

export const DEFAULT_TRADE_BROWSE_FILTERS: TradeBrowseFilters = {
  q: "",
  zipCode: "",
  game: "all",
  verifiedOnly: false,
  gradedOnly: false,
};

export const DEFAULT_BOUNTY_BROWSE_FILTERS: BountyBrowseFilters = {
  q: "",
  zipCode: "",
  game: "all",
  verifiedOnly: false,
  gradedOnly: false,
};

function appendIfPresent(
  target: Record<string, string | number | boolean>,
  key: string,
  value: unknown,
): void {
  const normalized = typeof value === "string" ? value.trim() : value;
  if (normalized === undefined || normalized === null || normalized === "") return;
  target[key] = normalized as string | number | boolean;
}

export function buildTradeBrowseParams(
  filters: TradeBrowseFilters,
  offset = 0,
  limit = BROWSE_PAGE_SIZE,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { limit, offset };
  appendIfPresent(params, "q", filters.q);
  appendIfPresent(params, "zipCode", filters.zipCode);
  if (filters.game !== "all") appendIfPresent(params, "game", filters.game);
  if (filters.verifiedOnly) params.verifiedOnly = true;
  if (filters.gradedOnly) params.gradedOnly = true;
  return params;
}

export function buildBountyBrowseParams(
  filters: BountyBrowseFilters,
  offset = 0,
  limit = BROWSE_PAGE_SIZE,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { limit, offset };
  appendIfPresent(params, "q", filters.q);
  appendIfPresent(params, "zipCode", filters.zipCode);
  if (filters.game !== "all") appendIfPresent(params, "game", filters.game);
  if (filters.verifiedOnly) params.verifiedOnly = true;
  if (filters.gradedOnly) params.gradedOnly = true;
  return params;
}

export function canClearTradeBrowseFilters(filters: TradeBrowseFilters): boolean {
  return JSON.stringify(filters) !== JSON.stringify(DEFAULT_TRADE_BROWSE_FILTERS);
}

export function canClearBountyBrowseFilters(filters: BountyBrowseFilters): boolean {
  return JSON.stringify(filters) !== JSON.stringify(DEFAULT_BOUNTY_BROWSE_FILTERS);
}

export function mergePagedItemsById<T extends { id: string | number }>(current: T[], incoming: T[]): T[] {
  const merged = [...current];
  const seen = new Set(current.map((item) => String(item.id)));
  for (const item of incoming) {
    const key = String(item.id);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(item);
  }
  return merged;
}
